import type { Episode, PodcastSummary } from './types'

export const EPISODE_CAP = 28

export type DetailEntry = {
  loading: boolean
  error?: string
  podcast?: PodcastSummary
  episodes: Episode[]
  nextPubDateMs: number | null
}

export type State = {
  query: string
  search: {
    loading: boolean
    error?: string
    results: PodcastSummary[]
    nextOffset: number | null
  }
  detailsByPodcastId: Record<string, DetailEntry>
  activePodcastId?: string
  selected: Episode[]
}

export type Action =
  | { type: 'setQuery'; query: string }
  | { type: 'searchStart' }
  | {
      type: 'searchSuccess'
      results: PodcastSummary[]
      nextOffset: number | null
      append: boolean
    }
  | { type: 'searchError'; message: string }
  | { type: 'openPodcast'; id: string }
  | { type: 'closePodcast' }
  | { type: 'detailStart'; id: string }
  | {
      type: 'detailSuccess'
      id: string
      podcast: PodcastSummary
      episodes: Episode[]
      nextPubDateMs: number | null
      append: boolean
    }
  | { type: 'detailError'; id: string; message: string }
  | { type: 'addEpisode'; episode: Episode }
  | { type: 'removeEpisode'; id: string }
  | { type: 'moveEpisode'; index: number; direction: 'up' | 'down' }
  | { type: 'clearSelection' }

export const initialState: State = {
  query: '',
  search: { loading: false, results: [], nextOffset: null },
  detailsByPodcastId: {},
  selected: [],
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'setQuery':
      return { ...state, query: action.query }
    case 'searchStart':
      return { ...state, search: { ...state.search, loading: true, error: undefined } }
    case 'searchSuccess': {
      const results = action.append
        ? [...state.search.results, ...action.results]
        : action.results
      return {
        ...state,
        search: {
          loading: false,
          error: undefined,
          results,
          nextOffset: action.nextOffset,
        },
      }
    }
    case 'searchError':
      return {
        ...state,
        search: {
          ...state.search,
          loading: false,
          error: action.message,
        },
      }
    case 'openPodcast':
      return { ...state, activePodcastId: action.id }
    case 'closePodcast':
      return { ...state, activePodcastId: undefined }
    case 'detailStart': {
      const prev = state.detailsByPodcastId[action.id]
      return {
        ...state,
        detailsByPodcastId: {
          ...state.detailsByPodcastId,
          [action.id]: {
            loading: true,
            error: undefined,
            podcast: prev?.podcast,
            episodes: prev?.episodes ?? [],
            nextPubDateMs: prev?.nextPubDateMs ?? null,
          },
        },
      }
    }
    case 'detailSuccess': {
      const prev = state.detailsByPodcastId[action.id]
      const episodes = action.append
        ? [...(prev?.episodes ?? []), ...action.episodes]
        : action.episodes
      return {
        ...state,
        detailsByPodcastId: {
          ...state.detailsByPodcastId,
          [action.id]: {
            loading: false,
            error: undefined,
            podcast: action.podcast,
            episodes,
            nextPubDateMs: action.nextPubDateMs,
          },
        },
      }
    }
    case 'detailError': {
      const prev = state.detailsByPodcastId[action.id]
      return {
        ...state,
        detailsByPodcastId: {
          ...state.detailsByPodcastId,
          [action.id]: {
            loading: false,
            error: action.message,
            podcast: prev?.podcast,
            episodes: prev?.episodes ?? [],
            nextPubDateMs: prev?.nextPubDateMs ?? null,
          },
        },
      }
    }
    case 'addEpisode': {
      if (state.selected.length >= EPISODE_CAP) return state
      if (state.selected.some((e) => e.id === action.episode.id)) return state
      return { ...state, selected: [...state.selected, action.episode] }
    }
    case 'removeEpisode':
      return {
        ...state,
        selected: state.selected.filter((e) => e.id !== action.id),
      }
    case 'moveEpisode': {
      const { index, direction } = action
      const swap = direction === 'up' ? index - 1 : index + 1
      if (
        index < 0 ||
        index >= state.selected.length ||
        swap < 0 ||
        swap >= state.selected.length
      ) {
        return state
      }
      const next = state.selected.slice()
      const tmp = next[index]
      next[index] = next[swap]
      next[swap] = tmp
      return { ...state, selected: next }
    }
    case 'clearSelection':
      return { ...state, selected: [] }
  }
}
